
import axios from 'axios';

import { getAuthToken } from '../utils/authUtils'; //

const API_BASE = process.env.REACT_APP_FIREBASE_BACKEND_API_BASE || 'https://quantumleap-emr-dev.web.app';
/**
 * Comprehensive Lab Reports Service
 * Handles all lab-related data fetching through secure backend APIs
 */

/**
 * Get lab reports for a patient
 * @param {string} patientId - Patient ID
 * @param {Object} options - Query options
 * @param {Date} options.startDate - Start date for filtering
 * @param {Date} options.endDate - End date for filtering
 * @param {string} options.reviewStatus - Review status filter
 * @param {number} options.page - Page number for pagination
 * @param {number} options.limit - Number of items per page
 * @returns {Promise<Array>} Promise resolving to array of lab reports
 */
export async function getLabReports(patientId, options = {}) {
  try {
    const token = await getAuthToken();
    const params = new URLSearchParams();
    
    params.append('patientId', patientId);
    if (options.startDate) params.append('startDate', options.startDate.toISOString());
    if (options.endDate) params.append('endDate', options.endDate.toISOString());
    if (options.reviewStatus) params.append('reviewStatus', options.reviewStatus);
    if (options.page) params.append('page', options.page.toString());
    if (options.limit) params.append('limit', options.limit.toString());
    
    const response = await axios.get(
      `${API_BASE}/clinical/lab-reports?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.reports || [];
  } catch (error) {
    console.error('Error fetching lab reports:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

/**
 * Get lab results for specific reports
 * @param {Array<string>} reportIds - Array of report IDs
 * @param {Object} options - Query options
 * @param {string} options.testCode - Test code filter
 * @param {string} options.resultStatus - Result status filter
 * @param {string} options.patientId - Patient ID (if provided, fetches by patient instead of reportIds)
 * @param {Date} options.startDate - Start date filter
 * @param {Date} options.endDate - End date filter
 * @returns {Promise<Array>} Promise resolving to array of lab results
 */
export async function getLabResults(reportIds, options = {}) {
  try {
    const token = await getAuthToken();
    
    // If patientId is provided, use the patient-based endpoint
    if (options.patientId) {
      const params = new URLSearchParams();
      if (options.startDate) params.append('startDate', options.startDate.toISOString());
      if (options.endDate) params.append('endDate', options.endDate.toISOString());
      if (options.testCode) params.append('testCode', options.testCode);
      
      const response = await axios.get(
        `${API_BASE}/clinical/patients/${options.patientId}/lab-results?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      // Backend returns { labResults: [...] }
      return response.data.labResults || response.data.results || response.data || [];
    }
    
    // Otherwise, use report-based endpoint
    const params = new URLSearchParams();
    reportIds.forEach(id => params.append('reportIds', id));
    if (options.testCode) params.append('testCode', options.testCode);
    if (options.resultStatus) params.append('resultStatus', options.resultStatus);
    
    const response = await axios.get(
      `${API_BASE}/clinical/lab-results-by-reports?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.results || [];
  } catch (error) {
    console.error('Error fetching lab results:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

/**
 * Get test definitions for lab tests
 * @param {Object} options - Query options
 * @param {string} options.testType - Test type filter (e.g., 'Lab')
 * @param {boolean} options.active - Active status filter
 * @param {string} options.category - Category filter
 * @returns {Promise<Array>} Promise resolving to array of test definitions
 */
export async function getTestDefinitions(options = {}) {
  try {
    const token = await getAuthToken();
    const params = new URLSearchParams();
    
    if (options.testType) params.append('testType', options.testType);
    if (options.active !== undefined) params.append('active', options.active.toString());
    if (options.category) params.append('category', options.category);
    
    const response = await axios.get(
      `${API_BASE}/clinical/test-definitions?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.testDefinitions || [];
  } catch (error) {
    console.error('Error fetching test definitions:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

/**
 * Get test panels
 * @param {Object} options - Query options
 * @param {boolean} options.active - Active status filter
 * @param {string} options.category - Category filter
 * @returns {Promise<Array>} Promise resolving to array of test panels
 */
export async function getTestPanels(options = {}) {
  try {
    const token = await getAuthToken();
    const params = new URLSearchParams();
    
    if (options.active !== undefined) params.append('active', options.active.toString());
    if (options.category) params.append('category', options.category);
    
    const response = await axios.get(
      `${API_BASE}/clinical/test-panels?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data.testPanels || [];
  } catch (error) {
    console.error('Error fetching test panels:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}

/**
 * Get comprehensive lab data for a patient including reports, results, and test definitions
 * @param {string} patientId - Patient ID
 * @param {Object} options - Query options
 * @param {Date} options.startDate - Start date for filtering
 * @param {Date} options.endDate - End date for filtering
 * @returns {Promise<Object>} Promise resolving to comprehensive lab data
 */
export async function getComprehensiveLabData(patientId, options = {}) {
  try {
    const token = await getAuthToken();
    const params = new URLSearchParams();
    
    params.append('patientId', patientId);
    if (options.startDate) params.append('startDate', options.startDate.toISOString());
    if (options.endDate) params.append('endDate', options.endDate.toISOString());
    
    const response = await axios.get(
      `${API_BASE}/clinical/lab-data/comprehensive?${params.toString()}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error('Error fetching comprehensive lab data:', error);
    if (axios.isAxiosError(error) && error.response?.data?.error) {
      throw new Error(error.response.data.error);
    }
    throw error;
  }
}
